/* ═══════════════════════════════════════════════════════════════
   PiARIS — Shared Helpers
   Pure functions used across all products — no React dependency
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */

/** Count distinct unremediated issues of a given severity */
function countBySev(issues,sev){return issues.filter(function(i){return i.sev===sev&&!i.remediated}).length}
/** Sum element counts of unremediated issues of a given severity */
function sumBySev(issues,sev){return issues.filter(function(i){return i.sev===sev&&!i.remediated}).reduce(function(x,i){return x+(i.count||1)},0)}
/** Count of issues marked remediated */
function countRemediated(issues){return issues.filter(function(i){return i.remediated}).length}
/** Count of issues still open (not remediated) */
function countOpen(issues){return issues.filter(function(i){return!i.remediated}).length}
/** Sum all element counts across all issues */
function totalElements(issues){return issues.reduce(function(x,i){return x+(i.count||1)},0)}
/** Keyboard-accessible click: makes any element respond to Enter/Space.
    Returns {onClick, onKeyDown, tabIndex, role} props. */
function kbNav(handler){return{onClick:handler,onKeyDown:function(ev){if(ev.key==="Enter"||ev.key===" "){ev.preventDefault();handler(ev)}},tabIndex:0,role:"button"}}

/** Persistent storage — set. Uses PiARIS window.storage API with localStorage fallback.
    @param {string} k - Storage key
    @param {*} v - Value (auto-serialized to JSON) */
async function sS(k,v){try{if(window.storage&&window.storage.set){var r=await window.storage.set(k,JSON.stringify(v));return r}else{localStorage.setItem(k,JSON.stringify(v));return{key:k,value:JSON.stringify(v)}}}catch(e){try{localStorage.setItem(k,JSON.stringify(v));return{key:k}}catch(e2){console.error("Storage set failed:",k,e2);return null}}}
/** Persistent storage — get. Returns parsed JSON or null.
    @param {string} k - Storage key
    @returns {*|null} Parsed value or null */
async function sG(k){try{if(window.storage&&window.storage.get){var r=await window.storage.get(k);return r?JSON.parse(r.value):null}else{var v=localStorage.getItem(k);return v?JSON.parse(v):null}}catch(e){try{var v2=localStorage.getItem(k);return v2?JSON.parse(v2):null}catch(e2){console.warn("Storage get:",k,e2.message);return null}}}

/** Simple string hash → deterministic positive integer seed */
function hashStr(s){var h=0;for(var i=0;i<s.length;i++){h=((h<<5)-h)+s.charCodeAt(i);h|=0}return Math.abs(h)}

/* ═══════════════════════════════════════════════════════════════
   ALPHANUMERIC ID SCHEME
   Human-readable short IDs for pages, documents, and their issues.
   ═══════════════════════════════════════════════════════════════
   Display IDs (session-scoped, sequential):
     WP-001, WP-002, ...         ← web pages within a session
     DOC-001, DOC-002, ...       ← documents within a session
     WP-001-I01, WP-001-I02, ... ← issues on a specific page
     DOC-001-I01, ...            ← issues on a specific document

   Entity keys (stable across re-scans):
     canonicalUrl(url)    ← normalized URL (strip www, query, fragment, trailing /)
     entityKey(url)       ← 8-char base36 hash of canonical URL, e.g. "a3f2b9e1"
     issueFingerprint(i)  ← stable key per issue across scans ({wcag|sev|rule|el})
*/

/** Pad a number to 3 digits (WP-001, WP-014, WP-247). */
function _pad3(n){n=String(n);return n.length>=3?n:("000"+n).slice(-3)}
/** Pad a number to 2 digits for issue indexes. */
function _pad2(n){n=String(n);return n.length>=2?n:("00"+n).slice(-2)}

/** Build a web page display ID from a zero-based index. Returns "WP-001". */
function wpId(idx){return "WP-"+_pad3((idx||0)+1)}
/** Build a document display ID. Returns "DOC-001". */
function docId(idx){return "DOC-"+_pad3((idx||0)+1)}
/** Build an issue ID scoped under a parent (page or doc). Returns "WP-001-I03". */
function issueId(parentId,issueIdx){return parentId+"-I"+_pad2((issueIdx||0)+1)}

/** Normalize a URL for stable cross-scan entity matching.
    - Lowercases protocol + host
    - Strips "www."
    - Removes query string and fragment
    - Removes trailing slash (except root)
    Unparseable URLs are returned as-is (trimmed). */
function canonicalUrl(u){
  if(!u)return"";
  try{
    var url=new URL(u);
    var host=(url.hostname||"").toLowerCase().replace(/^www\./,"");
    var path=(url.pathname||"/").replace(/\/+$/,"")||"/";
    return(url.protocol||"https:").toLowerCase()+"//"+host+path;
  }catch(e){return String(u).trim().toLowerCase()}
}

/** Short stable key for an entity (page or doc), 8 char base36 hash of canonical URL.
    Same URL always produces the same key across scans. */
function entityKey(url){
  var c=canonicalUrl(url);
  if(!c)return"";
  var h=hashStr(c).toString(36);
  while(h.length<8)h="0"+h;
  return h.slice(0,8);
}

/** Stable fingerprint for an individual issue — {wcag|sev|rule|el}.
    Used to match "same issue" across scans of the same page. */
function issueFingerprint(issue){
  if(!issue)return"";
  var parts=[(issue.wcag||""),(issue.sev||""),(issue.id||issue.ruleId||""),(issue.el||"").slice(0,64)];
  return hashStr(parts.join("|")).toString(36).slice(0,10);
}

/* ═══════════════════════════════════════════════════════════════
   REMEDIATION TRACKER (PR-D)
   Per-issue assignee / status / notes / trackerUpdatedAt fields,
   surfaced in the UI and the XLSX tracker export. New-scan-only —
   legacy issues remain field-less and the UI tolerates undefined.
   ═══════════════════════════════════════════════════════════════ */

/** Allowed status values — Jira-style; do not invent variants. */
var TRACKER_STATUSES = ["To Do", "In Progress", "In Review", "Done", "Won't Do"];

/** Default tracker fields applied to every NEW issue at persist time.
    Safe to spread onto an issue with Object.assign: existing field
    values win, so calling this on an already-tracked issue is a no-op. */
function defaultIssueTrackerFields() {
  return {
    assignee: "",
    status: "To Do",
    notes: "",
    trackerUpdatedAt: null
  };
}

/** Validate a status string against the Jira-style enum.
    Returns the input if valid, else undefined. */
function normalizeTrackerStatus(s) {
  if (typeof s !== "string") return undefined;
  return TRACKER_STATUSES.indexOf(s) >= 0 ? s : undefined;
}

/** Classify domain into site type for template selection */
function detectSiteType(d){var dl=d.toLowerCase();if(/gov|mil|county|city|state|district|agency|authority/.test(dl))return"gov";if(/edu|university|college|school|academy|institute/.test(dl))return"edu";if(/health|hospital|medical|clinic|pharma|care/.test(dl))return"health";return"corp"}

/** Aggregate scan statistics from a page/document array.
    @param {Array} arr - Array of scan items (wp or dc)
    @returns {Object} {n:itemCount, tI:totalIssues, tC:criticals, tMa:majors, tMi:minors, tRem:remediated, tw:wcagTuples, wc:wcagCriteriaCount} */
function agg(arr){
  var tI=0,tC=0,tMa=0,tMi=0,tRem=0,wm={};
  var hasStubs=false;
  arr.forEach(function(x){
    tI+=x.totalIssues;tC+=x.critCount;tMa+=x.majCount;tMi+=x.minCount;tRem+=(x.remediatedCount||0);
    if(x._stub){hasStubs=true}
    x.issues.forEach(function(i){wm[i.wcag]=(wm[i.wcag]||0)+i.count});
  });
  var tw=Object.entries(wm).sort(function(a,b){return b[1]-a[1]});
  return{n:arr.length,tI:tI,tC:tC,tMa:tMa,tMi:tMi,tRem:tRem,tw:tw,wc:tw.length,hasStubs:hasStubs};
}

/** Classify an issue into a fix category for smart default resolution.
    Categories determine whether PiARIS (rule engine) or AI should be
    the default winner when both produce different fixes.
    @param {Object} iss - issue object with wcag, desc, info properties
    @returns {string} category id: contrast|aria|parsing|lang|alt-text|link|heading|form|structure|other */
function classifyFixCategory(iss){
  var w=iss.wcag||"";var desc=(iss.desc||"").toLowerCase();var name=(iss.info&&iss.info.n||"").toLowerCase();
  if(w==="1.4.3"||w==="1.4.11"||/contrast/i.test(name))return "contrast";
  if(w==="4.1.2"||w==="4.1.3"||/role|aria|name.*value/i.test(name))return "aria";
  if(w==="4.1.1"||/pars|duplicate.*id/i.test(name))return "parsing";
  if(w==="3.1.1"||w==="3.1.2"||/language/i.test(name))return "lang";
  if(w==="1.1.1"||/alt\s*text|non-text/i.test(name))return "alt-text";
  if(w==="2.4.4"||/link.*purpose/i.test(name))return "link";
  if((w==="2.4.6"||w==="1.3.1")&&(/heading/i.test(desc)||/heading/i.test(name)))return "heading";
  if(w==="3.3.1"||w==="3.3.2"||/label|instruction|form/i.test(name))return "form";
  if(w==="1.3.1"||w==="1.3.2"||w==="2.4.1")return "structure";
  return "other";
}
/** Human-readable label for a fix category.
    @param {string} cat - category id
    @returns {string} display label */
function fixCategoryLabel(cat){
  var labels={contrast:"Color / Contrast",aria:"ARIA / Roles",parsing:"HTML Parsing",lang:"Language",structure:"Semantic Structure",other:"Other","alt-text":"Alt Text",link:"Link Purpose",heading:"Headings",form:"Form Labels"};
  return labels[cat]||"Other";
}
/** Normalize fix text for comparison (trim, lowercase, collapse whitespace).
    @param {string} t - fix text
    @returns {string} normalized */
function normFix(t){return(t||"").trim().replace(/\s+/g," ").toLowerCase()}

/** Merge AI enrichment suggestions onto a list of workbench issues.
    Pure function — no React state, no side effects. Same matching
    strategy used by both buildRemediationFile's polling interval and
    the on-burst / on-reopen rehydrate paths in App.jsx so behavior
    stays consistent across entry points.

    Strategies, in order of preference:
      1. Index-based   — suggestions[i] aligns with issues[i] when WCAG matches
      2. WCAG+selector — exact compound key
      3. Category-based — fall back to fix-category buckets (alt-text, heading, etc.)
      4. Alt-text       — pull from altTextSuggestions[] for "alt-text" issues

    If the resolved AI fix text is byte-equivalent (after normFix) to
    the PiARIS fix, the field is collapsed to the literal string "same"
    which the workbench renders as "✓ Endorses PiARIS fix".

    Idempotent: issues that already carry an aiFix are skipped — re-running
    the merge on partially-merged data won't churn the existing matches.

    @param {Array} issues - workbench issues with {wcag, selector, pFix, fixCategory, ...}
    @param {Object} enrichData - { aiSuggestions:[], altTextSuggestions?:[] }
    @returns {Array} new issues with .aiFix populated where matched */
function mergeAiSuggestionsIntoIssues(issues, enrichData){
  if(!Array.isArray(issues))return [];
  if(!enrichData||!Array.isArray(enrichData.aiSuggestions))return issues.slice();
  var suggestions=enrichData.aiSuggestions||[];
  var altSugs=enrichData.altTextSuggestions||[];
  /* Track consumed suggestions via sidecar sets keyed by index rather than
     by mutating ._used on the suggestion objects themselves. Mutating the
     inputs would leak across callers that share the same enrichData ref
     (e.g. the 3s polling interval re-reading from aiEnrichRef.current).
     Session 49 burned us on this exact pattern in the patches matcher. */
  var usedSug={};
  var usedAlt={};
  return issues.map(function(iss,issIdx){
    if(iss.aiFix)return iss;
    var aiFixText="";
    var cat=iss.fixCategory||classifyFixCategory(iss);
    /* Strategy 1: index-based */
    if(issIdx<suggestions.length&&!usedSug[issIdx]){
      var idxSug=suggestions[issIdx];
      var idxWcag=idxSug.wcag||"";
      if(!idxWcag||idxWcag===iss.wcag){
        aiFixText=idxSug.after||idxSug.suggestion||"";
        usedSug[issIdx]=true;
      }
    }
    /* Strategy 2: WCAG+selector */
    if(!aiFixText&&iss.selector){
      for(var si=0;si<suggestions.length;si++){
        if(!usedSug[si]&&(suggestions[si].wcag||"")===(iss.wcag||"")&&
           (suggestions[si].selector||"")===(iss.selector||"")){
          aiFixText=suggestions[si].after||suggestions[si].suggestion||"";
          usedSug[si]=true;break;
        }
      }
    }
    /* Strategy 3: category-based */
    if(!aiFixText){
      for(var si2=0;si2<suggestions.length;si2++){
        if(usedSug[si2])continue;
        var sug=suggestions[si2];
        var sugCat=sug.type==="alt-text"?"alt-text":
          sug.type==="heading-repair"?"heading":
          sug.type==="link-purpose"?"link":
          sug.type==="form-label"?"form":
          sug.type==="contrast"?"contrast":
          sug.type==="aria"?"aria":"other";
        if(sugCat===cat){aiFixText=sug.after||sug.suggestion||"";usedSug[si2]=true;break;}
      }
    }
    /* Strategy 4: alt-text from altTextSuggestions[] */
    if(!aiFixText&&cat==="alt-text"){
      for(var ai=0;ai<altSugs.length;ai++){
        if(!usedAlt[ai]){
          aiFixText=altSugs[ai].isDecorative?
            'alt="" role="presentation"':
            'alt="'+altSugs[ai].suggestedAlt+'"';
          usedAlt[ai]=true;break;
        }
      }
    }
    /* Endorsement marker when AI matches PiARIS */
    if(aiFixText&&iss.pFix&&normFix(aiFixText)===normFix(iss.pFix))aiFixText="same";
    return Object.assign({},iss,{aiFix:aiFixText});
  });
}

/** Derive the per-instance locators for a single issue.
 *
 *  PR-5.15b normalizes two engine shapes into one instance list:
 *    - PDF (PA11Y Lab structural engine, PR-5.15a): the issue carries
 *      a single `instance` block { page, bbox, mcid, elementType,
 *      pageElementIndex, text }. One issue == one instance.
 *    - Web (Watchtower axe/HTMLCS): the issue carries `nodes[]`, each
 *      { bbox?, selector? }, plus top-level `selector`/`el`. One issue
 *      can cover N DOM nodes == N instances.
 *
 *  Falls back to a single synthetic instance built from selector/el
 *  when neither richer shape is present, so every issue yields at
 *  least one instance and the workbench never renders an empty group.
 *
 *  @param {object} iss
 *  @returns {object[]} instances — each { page?, url?, bbox?, mcid?,
 *           selector?, el?, elementType?, pageElementIndex?, label } */
function deriveInstances(iss){
  if(!iss)return [];
  var out=[];
  /* PDF shape — single instance block from pdf-audit */
  if(iss.instance){
    var ins=iss.instance;
    out.push({
      page: ins.page != null ? ins.page : (iss.page != null ? iss.page : null),
      bbox: ins.bbox || null,
      mcid: ins.mcid != null ? ins.mcid : null,
      elementType: ins.elementType || null,
      pageElementIndex: ins.pageElementIndex != null ? ins.pageElementIndex : null,
      text: ins.text || "",
      label: instanceLabel(ins, iss)
    });
    return out;
  }
  /* Web shape — explode nodes[] into one instance each */
  if(Array.isArray(iss.nodes) && iss.nodes.length){
    for(var i=0;i<iss.nodes.length;i++){
      var n=iss.nodes[i]||{};
      out.push({
        page: iss.page != null ? iss.page : null,
        url: iss.url || null,
        bbox: n.bbox || null,
        selector: n.selector || iss.selector || "",
        el: n.el || iss.el || "",
        elementType: null,
        pageElementIndex: i + 1,
        text: "",
        label: instanceLabel({ selector: n.selector || iss.selector, el: n.el || iss.el }, iss)
      });
    }
    return out;
  }
  /* Fallback — single synthetic instance from selector/el */
  out.push({
    page: iss.page != null ? iss.page : null,
    url: iss.url || null,
    bbox: null,
    selector: iss.selector || "",
    el: iss.el || "",
    elementType: null,
    pageElementIndex: 1,
    text: "",
    label: instanceLabel({ selector: iss.selector, el: iss.el }, iss)
  });
  return out;
}

/** Build a short human-readable label for an instance — what the
 *  workbench sub-row shows. Prefers, in order: element type + page,
 *  selector, truncated element snippet, then a generic fallback.
 *  @param {object} ins - instance-ish object
 *  @param {object} iss - parent issue (for page context)
 *  @returns {string} */
function instanceLabel(ins, iss){
  ins = ins || {};
  if(ins.elementType){
    var idx = ins.pageElementIndex ? (" #" + ins.pageElementIndex) : "";
    var pg = (ins.page != null) ? (" · p." + ins.page) : "";
    return ins.elementType + idx + pg;
  }
  if(ins.selector) return ins.selector;
  if(ins.el){
    var stripped = String(ins.el).replace(/\s+/g, " ").trim();
    return stripped.length > 60 ? stripped.slice(0, 57) + "…" : stripped;
  }
  if(iss && iss.page != null) return "Page " + iss.page;
  return "Element";
}

/** Group a flat issue list into the workbench display hierarchy.
 *
 *  PDFs nest by PAGE first (each page is a container), web audits nest
 *  by URL — per the per-page-vs-per-site distinction. Within each
 *  container, issues sharing the same checkpoint (or WCAG when no
 *  checkpoint id) AND severity collapse into one group whose
 *  `instances[]` is the union of every contributing issue's instances.
 *  The document/site-wide rollup (`groupBy:"none"`) flattens all
 *  issues into a single container for the executive "across pages"
 *  view.
 *
 *  This is the data backbone for PR-5.15b/c: the expandable +/- rows
 *  iterate `containers[].groups[]`, and clicking a group highlights
 *  every `group.instances[]` while clicking a sub-row highlights one.
 *
 *  Pure and non-mutating — issues are read, never written.
 *
 *  @param {object[]} issues
 *  @param {object} opts - { groupBy: "page"|"url"|"none" }
 *  @returns {{containers: object[], totalIssues: number, totalInstances: number}} */
function groupWorkbenchIssues(issues, opts){
  opts = opts || {};
  var groupBy = opts.groupBy || "page";
  if(!Array.isArray(issues)) return { containers: [], totalIssues: 0, totalInstances: 0 };

  /* containerOrder preserves first-seen ordering; containerMap indexes
     by key for O(1) lookup during the single pass. */
  var containerOrder = [];
  var containerMap = {};
  var totalInstances = 0;

  function containerKeyFor(iss){
    if(groupBy === "none") return "__all__";
    if(groupBy === "url") return iss.url || "(no url)";
    /* default: page */
    var pg = (iss.instance && iss.instance.page != null) ? iss.instance.page
           : (iss.page != null ? iss.page : null);
    return pg == null ? "(no page)" : ("page:" + pg);
  }
  function containerLabelFor(iss, key){
    if(groupBy === "none") return "All issues";
    if(groupBy === "url") return iss.url || "(no url)";
    var pg = (iss.instance && iss.instance.page != null) ? iss.instance.page
           : (iss.page != null ? iss.page : null);
    return pg == null ? "Location unknown" : ("Page " + pg);
  }

  for(var i=0;i<issues.length;i++){
    var iss = issues[i];
    if(!iss) continue;
    var cKey = containerKeyFor(iss);
    var container = containerMap[cKey];
    if(!container){
      container = { key: cKey, label: containerLabelFor(iss, cKey), groups: [], _groupMap: {} };
      containerMap[cKey] = container;
      containerOrder.push(container);
    }
    /* Group key: checkpoint id is the most specific; fall back to WCAG;
       fall back to the description so untyped issues still collapse
       sensibly. Severity is part of the key so a critical and a minor
       instance of the same checkpoint don't merge. */
    var gKey = (iss.id || iss.checkpoint || iss.wcag || (iss.desc || "").slice(0, 40)) + "|" + (iss.sev || "");
    var group = container._groupMap[gKey];
    if(!group){
      group = {
        groupKey: gKey,
        wcag: iss.wcag || "",
        checkpoint: iss.id || iss.checkpoint || "",
        sev: iss.sev || "minor",
        cat: iss.cat || iss.category || "",
        desc: iss.desc || iss.description || "",
        fix: iss.fix || iss.remediation || "",
        instances: []
      };
      container._groupMap[gKey] = group;
      container.groups.push(group);
    }
    var derived = deriveInstances(iss);
    for(var k=0;k<derived.length;k++){
      /* Stamp the parent issue ref so the renderer can reach pFix/aiFix
         etc. without re-joining. Non-enumerable-ish convention: prefix
         with _ so JSON serialization callers can drop it if needed. */
      derived[k]._issue = iss;
      group.instances.push(derived[k]);
      totalInstances++;
    }
  }

  /* Strip the internal _groupMap before returning. */
  for(var c=0;c<containerOrder.length;c++){
    delete containerOrder[c]._groupMap;
  }
  return {
    containers: containerOrder,
    totalIssues: issues.length,
    totalInstances: totalInstances
  };
}

/** Map a PDF bbox (user-space points, origin BOTTOM-left, Y up) onto a
 *  rendered page as CSS percentages (origin TOP-left, Y down). Pure and
 *  resolution-independent: because the result is in percent of the page,
 *  it's correct at any render scale or display size, so the overlay never
 *  needs to know the canvas pixel dimensions.
 *
 *  The Y-flip is the crux: a PDF y-coordinate measures UP from the page
 *  bottom, but CSS `top` measures DOWN from the page top. So the box's
 *  top edge in screen space is (pageHeight − bboxTop) where bboxTop is the
 *  PDF-space top of the box (bbox.y + bbox.height).
 *
 *  @param {{x:number,y:number,width:number,height:number}} bbox - points
 *  @param {number} pageW - page width in points
 *  @param {number} pageH - page height in points
 *  @returns {{left:number,top:number,width:number,height:number}|null}
 *           percentages [0..100], or null if inputs are unusable */
function bboxToOverlayPct(bbox, pageW, pageH){
  if(!bbox) return null;
  if(!(pageW > 0) || !(pageH > 0)) return null;
  var x = bbox.x, y = bbox.y, w = bbox.width, h = bbox.height;
  if(typeof x !== "number" || typeof y !== "number" ||
     typeof w !== "number" || typeof h !== "number") return null;
  /* screen-space top edge = pageH − (PDF top edge) = pageH − (y + h) */
  var topPts = pageH - (y + h);
  return {
    left: (x / pageW) * 100,
    top: (topPts / pageH) * 100,
    width: (w / pageW) * 100,
    height: (h / pageH) * 100
  };
}

/** Format large numbers with K/M suffix for badge text and compact displays.
    Examples: 487→"487", 1200→"1.2k", 5000→"5.0k", 15300→"15.3k", 1250000→"1.3M"
    @param {number} n - number to format
    @returns {string} formatted string */
function shortNum(n){
  if(n==null||isNaN(n))return"0";
  if(n<1000)return String(n);
  if(n<1000000)return(n/1000).toFixed(1).replace(/\.0$/,"")+"k";
  return(n/1000000).toFixed(1).replace(/\.0$/,"")+"M";
}

/** Classify an AI-engine failure into plain-language guidance.
 *  The raw upstream error is frequently a verbatim Anthropic API JSON
 *  blob (e.g. {"type":"error","error":{"type":"invalid_request_error",
 *  "message":"Your credit balance is too low..."}}) which is useless —
 *  and misleading — to an end user. This maps the common failure modes
 *  to a title + actionable detail so the banner stops telling people to
 *  "set ANTHROPIC_API_KEY" when the key is fine and the account is
 *  simply out of credit. Pure; accepts a string or an object.
 *  @param {*} err
 *  @returns {{kind:string,title:string,detail:string,showRaw:boolean}} */
function classifyAiError(err){
  var raw = (err==null) ? "" :
    (typeof err==="string" ? err :
      (function(){try{return JSON.stringify(err)}catch(e){return String(err)}})());
  var lower = raw.toLowerCase();
  if(lower.indexOf("credit balance")>=0 || lower.indexOf("insufficient")>=0 ||
     lower.indexOf("too low")>=0 || lower.indexOf("plans & billing")>=0){
    return { kind:"billing",
      title:"AI Analysis unavailable — Anthropic account credit exhausted",
      detail:"The API key is valid and the request reached Anthropic, but the account backing it has no remaining credit. An admin can top up at console.anthropic.com → Plans & Billing. Structural checks are unaffected.",
      showRaw:false };
  }
  if(lower.indexOf("authentication")>=0 || lower.indexOf("anthropic_api_key")>=0 ||
     lower.indexOf("not configured")>=0 || lower.indexOf("unauthorized")>=0 ||
     lower.indexOf("401")>=0){
    return { kind:"auth",
      title:"AI Analysis unavailable — API key not configured",
      detail:"Set ANTHROPIC_API_KEY in the Function App configuration to enable this engine.",
      showRaw:false };
  }
  if(lower.indexOf("rate_limit")>=0 || lower.indexOf("rate limit")>=0 ||
     lower.indexOf("overloaded")>=0 || lower.indexOf("429")>=0){
    return { kind:"rate",
      title:"AI Analysis temporarily throttled",
      detail:"The AI service is rate-limited or momentarily overloaded. Wait a few seconds and retry — no configuration change is needed.",
      showRaw:false };
  }
  return { kind:"unknown",
    title:"AI Analysis engine did not complete",
    detail:raw,
    showRaw:true };
}

/** Normalize a CSS selector for template deduplication.
    Strips dynamic parts (numeric IDs, nth-child indices, data attributes with numbers)
    to create a structural pattern that matches across pages.
    e.g. "#menu-item-2847 > a" → "* > a"
    e.g. "div:nth-child(3) > img" → "div:nth-child(*) > img"
    e.g. ".site-header .logo-link" → ".site-header .logo-link" (unchanged — no dynamic parts) */
function normalizeSelector(sel){
  if(!sel)return "";
  var s=sel
    .replace(/#[\w-]*\d+[\w-]*/g,"*")                /* IDs with numbers → * */
    .replace(/:nth-child\(\d+\)/g,":nth-child(*)")    /* nth-child(N) → nth-child(*) */
    .replace(/:nth-of-type\(\d+\)/g,":nth-of-type(*)")
    .replace(/\[data-[\w-]+="[^"]*\d+[^"]*"\]/g,"")  /* data attrs with numbers → remove */
    .replace(/\s+/g," ").trim();
  return s;
}

/** Weighted Accessibility Compliance Score.
    Deduction-based (starts at 100). Anchored to WCAG severity, conformance level,
    issue frequency (log scale), and spread across pages.

    Formula per unique issue group:
      penalty = severity_weight × level_multiplier × (1 + ln(instances)) × spread

    Severity weights (Deque/axe aligned): critical=10, major=5, minor=1
    Level multiplier: A=1.5 (foundational), AA=1.0 (standard target)
    Frequency: logarithmic — 1 inst=1.0, 10=3.3, 100=5.6
    Spread: 0.5 + 0.5 × (affected_pages / total_pages)

    Hard cap: any open critical Level A issue → max score 84

    @param {Array} wp - web pages array
    @param {Array} dc - documents array
    @param {Object} W - WCAG reference { "1.1.1": { l:"A", n:"..." }, ... }
    @returns {Object} compliance result */
function computeComplianceScore(wp, dc, W, summary) {
  var SEV_W = { critical: 10, major: 5, minor: 1 };
  var LVL_W = { A: 1.5, AA: 1.0 };

  function scorePages(pages) {
    if (!pages || pages.length === 0) return { score: 100, penalties: [], hasCritA: false };
    var groups = {};
    var totalPages = pages.length;
    pages.forEach(function(pg, pi) {
      (pg.issues || []).forEach(function(iss) {
        if (iss.remediated) return;
        var key = (iss.wcag || "") + "||" + (iss.sev || "major");
        if (!groups[key]) {
          var wInfo = W && W[iss.wcag] ? W[iss.wcag] : {};
          groups[key] = { wcag: iss.wcag || "", sev: iss.sev || "major", level: wInfo.l || "AA",
            name: wInfo.n || iss.desc || "", count: 0, pages: {} };
        }
        groups[key].count += (iss.count || 1);
        groups[key].pages[pi] = true;
      });
    });

    var penalties = [];
    var hasCritA = false;
    var keys = Object.keys(groups);
    for (var k = 0; k < keys.length; k++) {
      var g = groups[keys[k]];
      var sevW = SEV_W[g.sev] || 1;
      var lvlW = LVL_W[g.level] || 1.0;
      var freq = 1 + Math.log(Math.max(1, g.count));
      var affectedPages = Object.keys(g.pages).length;
      var spread = 0.5 + 0.5 * (affectedPages / Math.max(1, totalPages));
      var penalty = sevW * lvlW * freq * spread;
      if (g.sev === "critical" && g.level === "A") hasCritA = true;
      penalties.push({ wcag: g.wcag, name: g.name, sev: g.sev, level: g.level,
        count: g.count, pages: affectedPages, penalty: Math.round(penalty * 10) / 10 });
    }
    penalties.sort(function(a, b) { return b.penalty - a.penalty; });
    var totalPenalty = penalties.reduce(function(x, p) { return x + p.penalty; }, 0);
    var score = Math.max(0, Math.round(100 - totalPenalty));
    if (hasCritA && score > 84) score = 84;
    return { score: score, penalties: penalties, hasCritA: hasCritA };
  }

  var activePages = wp.filter(function(p) { return !p.isArchived; });
  var archivePages = wp.filter(function(p) { return p.isArchived; });
  var activeResult = scorePages(activePages);
  var archiveResult = scorePages(archivePages);

  var hasData = (wp && wp.length > 0) || (dc && dc.length > 0);
  /* When pages haven't loaded yet but summary exists, use summary stats as fallback.
     This prevents showing "DOCS ONLY" while page stubs are still loading. */
  var sum = summary || null;
  var usingSummaryFallback = !!(wp.length === 0 && sum && sum.totalPages > 0);
  var s = activeResult.score;
  var status, statusColor;

  if (usingSummaryFallback) {
    /* Pages loading — use pre-computed score from session.summary */
    hasData = true;
    s = sum.score != null ? sum.score : null;
  }

  if (!hasData) { status = "No Data"; statusColor = "slateL"; s = null; }
  else if (usingSummaryFallback) {
    /* Show real status from pre-computed score while pages load */
    if (s >= 95) { status = "Compliant"; statusColor = "emerald"; }
    else if (s >= 85) { status = "Substantially Compliant"; statusColor = "accent"; }
    else if (s >= 70) { status = "Partially Compliant"; statusColor = "amber"; }
    else if (s >= 50) { status = "Non-Compliant"; statusColor = "coral"; }
    else if (s != null) { status = "Critically Non-Compliant"; statusColor = "sCrit"; }
    else { status = "Loading..."; statusColor = "slateM"; }
  }
  else if (activePages.length === 0 && archivePages.length === 0 && dc.length > 0) {
    /* Only docs loaded, no web pages, no summary fallback */
    status = "Docs Only"; statusColor = "slateM";
  }
  else if (s >= 95) { status = "Compliant"; statusColor = "emerald"; }
  else if (s >= 85) { status = "Substantially Compliant"; statusColor = "accent"; }
  else if (s >= 70) { status = "Partially Compliant"; statusColor = "amber"; }
  else if (s >= 50) { status = "Non-Compliant"; statusColor = "coral"; }
  else { status = "Critically Non-Compliant"; statusColor = "sCrit"; }

  return {
    hasData: hasData,
    activeScore: s,
    archiveScore: hasData ? archiveResult.score : null,
    status: status,
    statusColor: statusColor,
    hasCritA: activeResult.hasCritA,
    topPenalties: activeResult.penalties.slice(0, 5),
    activePages: usingSummaryFallback ? (sum.activePages || sum.totalPages || 0) : activePages.length,
    archivePages: usingSummaryFallback ? (sum.archive ? sum.archive.pages || 0 : 0) : archivePages.length,
    archiveIssues: usingSummaryFallback ? (sum.archive ? sum.archive.issues || 0 : 0) : archivePages.reduce(function(x, p) { return x + (p.totalIssues || 0); }, 0),
    archiveCritical: usingSummaryFallback ? (sum.archive ? sum.archive.critical || 0 : 0) : archivePages.reduce(function(x, p) { return x + (p.critCount || 0); }, 0),
    usingSummaryFallback: usingSummaryFallback,
    summaryStats: usingSummaryFallback ? {
      totalPages: sum.totalPages || 0,
      totalElements: sum.totalElements || 0,
      critical: sum.critical || 0,
      major: sum.major || 0,
      minor: sum.minor || 0,
      pagesWithIssues: sum.pagesWithIssues || 0
    } : null
  };
}

/* ═══════════════════════════════════════════════════════════════
   AUDIT REPORT — enrichment helpers
   Derive the fields Allyant-style audit reports need from our
   existing scan data. No extension changes required.
   ═══════════════════════════════════════════════════════════════ */

/** WCAG criterion → list of affected disability groups.
    Used to label each issue with ["Visual"], ["Motor","Visual"], etc.
    Allyant uses: Visual, Auditory, Cognitive, Motor/Dexterity, UX
    Grouping follows WCAG Understanding docs. */
var AFFECTED_USERS_BY_WCAG = {
  /* Perceivable */
  "1.1.1": ["Visual"],                                      /* alt text */
  "1.2.1": ["Auditory", "Visual"],                          /* audio/video alt */
  "1.2.2": ["Auditory"],                                    /* captions */
  "1.2.3": ["Visual"],                                      /* audio description */
  "1.2.4": ["Auditory"],                                    /* live captions */
  "1.2.5": ["Visual"],                                      /* audio description */
  "1.3.1": ["Visual", "Cognitive"],                         /* info & relationships */
  "1.3.2": ["Visual", "Cognitive"],                         /* meaningful sequence */
  "1.3.3": ["Visual", "Cognitive"],                         /* sensory characteristics */
  "1.3.4": ["Motor/Dexterity", "Visual"],                   /* orientation */
  "1.3.5": ["Cognitive", "Motor/Dexterity"],                /* input purpose */
  "1.4.1": ["Visual", "Cognitive"],                         /* use of color */
  "1.4.2": ["Auditory"],                                    /* audio control */
  "1.4.3": ["Visual", "Cognitive"],                         /* contrast minimum */
  "1.4.4": ["Visual"],                                      /* resize text */
  "1.4.5": ["Visual"],                                      /* images of text */
  "1.4.10": ["Visual"],                                     /* reflow */
  "1.4.11": ["Visual"],                                     /* non-text contrast */
  "1.4.12": ["Visual", "Cognitive"],                        /* text spacing */
  "1.4.13": ["Motor/Dexterity", "Visual", "Cognitive"],     /* content on hover */
  /* Operable */
  "2.1.1": ["Motor/Dexterity", "Visual"],                   /* keyboard */
  "2.1.2": ["Motor/Dexterity", "Visual"],                   /* no keyboard trap */
  "2.1.4": ["Motor/Dexterity"],                             /* character key shortcuts */
  "2.2.1": ["Cognitive", "Motor/Dexterity"],                /* timing adjustable */
  "2.2.2": ["Cognitive", "Visual"],                         /* pause, stop, hide */
  "2.3.1": ["Cognitive", "Visual"],                         /* three flashes */
  "2.4.1": ["Motor/Dexterity", "Visual"],                   /* bypass blocks */
  "2.4.2": ["Visual", "Cognitive"],                         /* page titled */
  "2.4.3": ["Motor/Dexterity", "Visual"],                   /* focus order */
  "2.4.4": ["Visual", "Cognitive"],                         /* link purpose */
  "2.4.5": ["Cognitive", "Visual"],                         /* multiple ways */
  "2.4.6": ["Cognitive", "Visual"],                         /* headings and labels */
  "2.4.7": ["Motor/Dexterity", "Visual"],                   /* focus visible */
  "2.4.11": ["Motor/Dexterity", "Visual"],                  /* focus not obscured */
  "2.5.1": ["Motor/Dexterity"],                             /* pointer gestures */
  "2.5.2": ["Motor/Dexterity"],                             /* pointer cancellation */
  "2.5.3": ["Cognitive", "Visual"],                         /* label in name */
  "2.5.4": ["Motor/Dexterity"],                             /* motion actuation */
  "2.5.5": ["Motor/Dexterity"],                             /* target size */
  /* Understandable */
  "3.1.1": ["Cognitive", "Visual"],                         /* language of page */
  "3.1.2": ["Cognitive", "Visual"],                         /* language of parts */
  "3.2.1": ["Cognitive", "Motor/Dexterity"],                /* on focus */
  "3.2.2": ["Cognitive", "Motor/Dexterity"],                /* on input */
  "3.2.3": ["Cognitive", "Visual"],                         /* consistent navigation */
  "3.2.4": ["Cognitive"],                                   /* consistent identification */
  "3.3.1": ["Cognitive", "Motor/Dexterity", "Visual"],      /* error identification */
  "3.3.2": ["Cognitive", "Motor/Dexterity", "Visual"],      /* labels or instructions */
  "3.3.3": ["Cognitive", "Motor/Dexterity"],                /* error suggestion */
  "3.3.4": ["Cognitive"],                                   /* error prevention */
  /* Robust */
  "4.1.1": ["Visual"],                                      /* parsing */
  "4.1.2": ["Visual"],                                      /* name, role, value */
  "4.1.3": ["Visual"]                                       /* status messages */
};

/** WCAG criterion → affected users array. Fallback heuristic if not in map. */
function wcagToAffectedUsers(wcag) {
  if (!wcag) return ["Visual"];
  if (AFFECTED_USERS_BY_WCAG[wcag]) return AFFECTED_USERS_BY_WCAG[wcag];
  /* Fallback by principle prefix */
  var principle = (wcag || "").split(".")[0];
  if (principle === "1") return ["Visual"];
  if (principle === "2") return ["Motor/Dexterity", "Visual"];
  if (principle === "3") return ["Cognitive", "Visual"];
  if (principle === "4") return ["Visual"];
  return ["Visual"];
}

/** Derive "High Risk" label from severity + WCAG level.
    High Risk = critical (any level) OR major at Level A.
    @returns {"High Risk"|"Standard"} */
function wcagToRiskLevel(sev, wcagLevel) {
  if (sev === "critical") return "High Risk";
  if (sev === "major" && wcagLevel === "A") return "High Risk";
  return "Standard";
}

/** Map our severity → Allyant priority code + label.
    @returns {{code:"P1"|"P2"|"P3", label:"critical"|"serious"|"warning", color:"sCrit"|"amber"|"emerald"}} */
function sevToPriority(sev) {
  if (sev === "critical") return { code: "P1", label: "critical", color: "sCrit" };
  if (sev === "major")    return { code: "P2", label: "serious",  color: "amber" };
  return                         { code: "P3", label: "warning",  color: "emerald" };
}

/** Derive semantic component from CSS selector + element snippet.
    Returns a short human label and a classification suffix.
    Heuristic — transparent about limits. */
function selectorToComponent(selector, el) {
  var s = ((selector || "") + " " + (el || "")).toLowerCase();
  if (/\b(footer|role=["']?contentinfo)/.test(s))     return { name: "Footer",           kind: "Landmark" };
  if (/\b(header|role=["']?banner)/.test(s))          return { name: "Header",           kind: "Landmark" };
  if (/\b(nav|role=["']?navigation)/.test(s))         return { name: "Navigation",       kind: "Landmark" };
  if (/\b(aside|role=["']?complementary)/.test(s))    return { name: "Aside",            kind: "Landmark" };
  if (/\brole=["']?main\b|<main/.test(s))             return { name: "Main Content",     kind: "Landmark" };
  if (/role=["']?dialog|\.modal|modal-dialog/.test(s))return { name: "Modal Dialog",     kind: "Modal" };
  if (/\baccordion|aria-expanded/.test(s))            return { name: "Accordion",        kind: "Accordion" };
  if (/<iframe|\biframe\b/.test(s))                   return { name: "Iframe",           kind: "Third-party" };
  if (/\.card\b|\.tile\b|role=["']?group/.test(s))    return { name: "Cards and Tiles",  kind: "Cards and Tiles" };
  if (/<form|\bform\b|role=["']?form/.test(s))        return { name: "Form",             kind: "Form" };
  if (/\bchat\b/.test(s))                             return { name: "Chat Widget",      kind: "Chat, third-party" };
  if (/<button|role=["']?button/.test(s))             return { name: "Button",           kind: "Control" };
  return { name: "", kind: "" };
}

/** Is this issue likely sitewide? Derived from templateStats.groups.
    An issue is sitewide if its WCAG+normalized-selector key appears on >1 pages.
    @param {Object} iss - issue object with wcag, selector
    @param {Object} templateStats - from useMemo in App.jsx
    @returns {boolean} */
function isSitewideIssue(iss, templateStats) {
  if (!templateStats || !templateStats.groups) return false;
  var nSel = normalizeSelector(iss.selector || "");
  var descKey = (iss.desc || "").substring(0, 60).toLowerCase().replace(/\s+/g, " ");
  var key = (iss.wcag || "") + "||" + nSel + "||" + descKey;
  var g = templateStats.groups[key];
  return !!(g && g.pages && g.pages.length > 1);
}

/** matchesSearch — flexible name/URL search matcher for the page and
    document list filters.

    Improvements over a plain substring match:
      • Multi-term (AND): "budget 2017" matches a name containing BOTH
        "budget" and "2017" anywhere — find any words inside the name.
      • Wildcard `*` inside a term: matches any run of characters.
          report*2017  → "...report..." then "...2017..." in order
          *budget*     → contains "budget" anywhere
          fy*          → a segment starting "fy"
      • Case-insensitive; a plain term with no `*` is an ordinary
        substring match (unchanged behaviour for existing queries).

    haystack: the string(s) to search (e.g. a doc name, or name+" "+url).
    query:    the user's raw search box text. Empty query → matches all. */
function _escapeRegexLiteral(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function matchesSearch(haystack, query) {
  var hay = String(haystack == null ? "" : haystack).toLowerCase();
  var q = String(query == null ? "" : query).trim().toLowerCase();
  if (!q) return true;
  var terms = q.split(/\s+/);
  return terms.every(function (term) {
    if (!term) return true;
    if (term.indexOf("*") === -1) return hay.indexOf(term) > -1;
    // `*` → ".*"; escape the literal segments between the stars.
    var pattern = term.split("*").map(_escapeRegexLiteral).join(".*");
    try { return new RegExp(pattern).test(hay); }
    catch (e) { return hay.indexOf(term.replace(/\*/g, "")) > -1; }
  });
}

/** Assign global sequential issue numbers (like Allyant's #1, #2, ... #70).
    Numbers are assigned descending: newest-first, page-order, then issue-order.
    Returns a {pageIdx_issIdx: number} map. */
function globalIssueNumbers(wp) {
  var map = {};
  var total = 0;
  wp.forEach(function(pg) { total += (pg.issues || []).length; });
  var n = total;
  wp.forEach(function(pg, pi) {
    (pg.issues || []).forEach(function(iss, ii) {
      map[pi + "_" + ii] = n--;
    });
  });
  return map;
}

/** htmlForAi — shape fetched/pasted HTML for the AI TEXT prompt: drop the heavy,
    non-semantic bytes (inlined <style>, <script>, <link>, comments) so the page's
    BODY markup — what the model needs to judge alt text, landmarks, headings and
    forms — fits the char budget instead of being crowded out by the now-inlined
    CSS in <head>. AI-input only: does NOT affect axe/HTMLCS or the preview. */
function htmlForAi(html, maxChars) {
  var s = String(html || "");
  s = s.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "");      // inlined CSS (the bloat)
  s = s.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "");    // scripts (defensive; pasted HTML)
  s = s.replace(/<link\b[^>]*>/gi, "");                         // stylesheet / icon / preload links
  s = s.replace(/<!--[\s\S]*?-->/g, "");                        // HTML comments
  s = s.replace(/\n{3,}/g, "\n\n").trim();                      // collapse blank runs
  var limit = (typeof maxChars === "number" && maxChars > 0) ? maxChars : 30000;
  return s.length > limit ? s.slice(0, limit) : s;
}

export { countBySev, sumBySev, countRemediated, countOpen, totalElements,
  kbNav, sS, sG, hashStr, detectSiteType, agg,
  classifyFixCategory, fixCategoryLabel, normFix, shortNum, classifyAiError, normalizeSelector,
  mergeAiSuggestionsIntoIssues,
  deriveInstances, instanceLabel, groupWorkbenchIssues, bboxToOverlayPct,
  computeComplianceScore,
  wcagToAffectedUsers, wcagToRiskLevel, sevToPriority,
  selectorToComponent, isSitewideIssue, globalIssueNumbers, matchesSearch,
  AFFECTED_USERS_BY_WCAG,
  wpId, docId, issueId, canonicalUrl, entityKey, issueFingerprint,
  TRACKER_STATUSES, defaultIssueTrackerFields, normalizeTrackerStatus, htmlForAi };
